import { FiX } from "react-icons/fi";
import type { ReactNode } from "react";

import {
  ChipList,
  StatusBadge,
  TypeBadge,
} from "@/app/library/components/molecules";
import type { ReviewQueueItem } from "@/app/library/data_sources/mock";
import { Badge, Button } from "@/core/ui/atoms";
import {
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  AdminSectionTitle,
  Banner,
  adminInputClasses,
} from "@/core/ui/molecules";
import { DrawerTemplate } from "@/core/ui/templates";

type ReviewTemplateDrawerProps = {
  readonly item: ReviewQueueItem;
  readonly onArchive: (id: string) => void;
  readonly onClose: () => void;
  readonly onPublish: (id: string) => void;
  readonly onReject: (id: string) => void;
  readonly onRequestChanges: (id: string) => void;
};

export function ReviewTemplateDrawer({
  item,
  onArchive,
  onClose,
  onPublish,
  onReject,
  onRequestChanges,
}: ReviewTemplateDrawerProps) {
  const canPublish =
    item.status === "Pending Review" || item.status === "Changes Requested";

  return (
    <DrawerTemplate
      content={
        <div>
          <Banner>
            <strong>Review only.</strong> Super Admin can approve metadata and
            publish, but cannot edit structure or open builders. If structure is
            wrong, request changes or reject.
          </Banner>

          <AdminSectionTitle>Template Preview</AdminSectionTitle>
          <AdminPanel className="p-[18px]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-extrabold text-ink">{item.name}</div>
                <div className="text-xs text-ink-muted">{item.description}</div>
              </div>
              <span className="flex gap-1.5">
                <TypeBadge type={item.type} />
                <StatusBadge status={item.status} />
              </span>
            </div>
            <AdminMiniList className="mt-3.5">
              <AdminMiniRow>
                <strong>Source</strong>
                <span>{item.source}</span>
              </AdminMiniRow>
              <AdminMiniRow>
                <strong>Submitted by</strong>
                <span>{item.submittedBy}</span>
              </AdminMiniRow>
              <AdminMiniRow>
                <strong>Version</strong>
                <span>{item.version}</span>
              </AdminMiniRow>
              <AdminMiniRow>
                <strong>Fields / Items</strong>
                <span>{item.fields}</span>
              </AdminMiniRow>
            </AdminMiniList>
          </AdminPanel>

          <AdminSectionTitle className="mt-5">
            Metadata Review
          </AdminSectionTitle>
          <AdminMiniList>
            <AdminMiniRow>
              <strong>Verticals</strong>
              <ChipList values={item.verticals} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Category</strong>
              <span>{item.category}</span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Status</strong>
              <StatusBadge status={item.status} />
            </AdminMiniRow>
          </AdminMiniList>

          <AdminSectionTitle className="mt-5">
            Structure Review
          </AdminSectionTitle>
          <div className="overflow-hidden rounded-control border border-border">
            <ReviewCheck
              checked
              detail="Rendered from submitted template data"
              leftBadge={<Badge tone="neutral">Read-only</Badge>}
              rightBadge={<Badge tone="success">Allowed</Badge>}
              title="Preview available"
            />
            <ReviewCheck
              detail="Form/Media/Model Builder editing is blocked in Super Admin"
              leftBadge={<Badge tone="critical">Blocked</Badge>}
              rightBadge={<Badge tone="neutral">No edit</Badge>}
              title="Open Builder"
            />
            <ReviewCheck
              checked
              detail="Verticals and categories are reviewed before publish"
              leftBadge={<Badge tone="success">Allowed</Badge>}
              rightBadge={<Badge tone="global">Required</Badge>}
              title="Metadata review"
            />
          </div>

          {item.changes ? (
            <Banner tone="warning">
              <strong>Requested changes:</strong> {item.changes}
            </Banner>
          ) : null}

          <label className="mt-3.5 block">
            <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
              Decision reason / request changes
            </span>
            <textarea
              className={`${adminInputClasses} min-h-24 w-full py-2.5`}
              defaultValue={item.changes ?? ""}
              placeholder="Reason required for reject, archive or request changes."
            />
          </label>
        </div>
      }
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button onClick={() => onRequestChanges(item.id)}>
            Request Changes
          </Button>
          <Button onClick={() => onReject(item.id)} variant="danger">
            Reject
          </Button>
          <Button onClick={() => onArchive(item.id)}>Archive</Button>
          <Button
            disabled={!canPublish}
            onClick={() => onPublish(item.id)}
            variant="primary"
          >
            Publish to Catalog
          </Button>
        </>
      }
      header={
        <div className="flex w-full items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold leading-snug text-ink">
              Review Template
            </h2>
            <p className="mt-1 text-[13px] text-ink-muted">
              {item.name} · {item.source}
            </p>
          </div>
          <button
            aria-label="Close drawer"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control border-0 bg-transparent text-ink-subtle transition-colors hover:bg-gray-100 hover:text-ink"
            onClick={onClose}
            type="button"
          >
            <FiX aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      }
    />
  );
}

function ReviewCheck({
  checked = false,
  detail,
  leftBadge,
  rightBadge,
  title,
}: {
  readonly checked?: boolean;
  readonly detail: string;
  readonly leftBadge: ReactNode;
  readonly rightBadge: ReactNode;
  readonly title: string;
}) {
  return (
    <div className="grid grid-cols-[28px_minmax(0,1fr)_auto_auto] items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
      <input checked={checked} disabled type="checkbox" />
      <span>
        <strong>{title}</strong>
        <span className="block text-xs text-ink-muted">{detail}</span>
      </span>
      {leftBadge}
      {rightBadge}
    </div>
  );
}
